import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { PageHeader } from "../../components/ui/PageHeader";
import { EmptyState, ErrorState, LoadingState } from "../../components/ui/State";
import { type Column, Table } from "../../components/ui/Table";
import { useToast } from "../../components/ui/Toast";
import { ApiError } from "../../lib/api";
import {
  type ScheduledReport,
  type ScheduledReportLastStatus,
  scheduledReportsApi,
} from "../../lib/api/scheduledReports";
import { formatDateTime, weekdayLabel } from "../../lib/format";
import { formatMessage, t } from "../../lib/i18n";
import { useLocale } from "../../lib/useLocale";

function translateError(err: unknown): string {
  if (err instanceof ApiError) {
    return t("errors." + err.code, "") || err.message || t("errors.generic");
  }
  return t("errors.network");
}

function frequencySummary(row: ScheduledReport): string {
  const parts = [t("scheduledReports.frequency." + row.frequency)];
  if (row.frequency === "weekly" && row.weekday != null) {
    parts.push(weekdayLabel(row.weekday));
  } else if (row.frequency === "monthly" && row.day_of_month != null) {
    parts.push(formatMessage("scheduledReports.frequency.dayOfMonthLabel", {
      day: String(row.day_of_month),
    }));
  }
  parts.push(String(row.hour).padStart(2, "0") + ":00");
  return parts.join(" · ");
}

function lastStatusTone(status: ScheduledReportLastStatus): "neutral" | "success" | "warning" | "danger" {
  if (status === "ok") return "success";
  if (status === "error") return "danger";
  if (status === "skipped") return "warning";
  return "neutral";
}

export function ScheduledReportsPage() {
  useLocale();
  const qc = useQueryClient();
  const { push: pushToast } = useToast();
  const [pendingDelete, setPendingDelete] = useState<ScheduledReport | null>(null);
  const list = useQuery({
    queryKey: ["scheduled-reports"],
    queryFn: () => scheduledReportsApi.list(),
  });
  const remove = useMutation({
    mutationFn: (id: string) => scheduledReportsApi.delete(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["scheduled-reports"] });
      setPendingDelete(null);
    },
    onError: (err) => pushToast({
      tone: "danger",
      title: t("errors.generic"),
      body: translateError(err),
    }),
  });

  const columns: Column<ScheduledReport>[] = [
    { key: "name", header: t("scheduledReports.list.column.name"), render: (row) => row.name },
    {
      key: "type",
      header: t("scheduledReports.list.column.type"),
      render: (row) => row.report_type_label || row.report_type,
    },
    {
      key: "frequency",
      header: t("scheduledReports.list.column.frequency"),
      render: frequencySummary,
    },
    {
      key: "recipients",
      header: t("scheduledReports.list.column.recipients"),
      render: (row) => formatMessage("scheduledReports.list.recipientsCount", {
        count: String(row.recipients.length),
      }),
    },
    {
      key: "lastSent",
      header: t("scheduledReports.list.column.lastSent"),
      render: (row) => (
        <span className="mono table-cell--datetime">{formatDateTime(row.last_sent_at)}</span>
      ),
    },
    {
      key: "lastStatus",
      header: t("scheduledReports.list.column.lastStatus"),
      render: (row) => (
        <div>
          <Badge tone={lastStatusTone(row.last_status)}>
            {row.last_status
              ? t("scheduledReports.status." + row.last_status)
              : t("scheduledReports.status.never")}
          </Badge>
          {row.last_status === "error" && row.last_error && (
            <div
              title={row.last_error}
              style={{ fontSize: 11, color: "var(--color-text-muted)", marginTop: 4, maxWidth: 220 }}
            >
              {t("errors." + row.last_error, row.last_error)}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (row) => (
        <Button size="sm" variant="danger" onClick={() => setPendingDelete(row)}>
          {t("common.delete")}
        </Button>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        eyebrow={t("scheduledReports.list.eyebrow")}
        title={t("scheduledReports.list.title")}
        subtitle={t("scheduledReports.historySubtitle")}
      />
      <Card padding="none">
        {list.isLoading && <LoadingState />}
        {list.error && (
          <ErrorState
            titleKey="states.errorTitle"
            message={translateError(list.error)}
            action={
              <Button variant="secondary" size="sm" onClick={() => void list.refetch()}>
                {t("common.retry")}
              </Button>
            }
          />
        )}
        {list.data && (
          <Table
            rows={list.data}
            columns={columns}
            rowKey={(row) => row.id}
            empty={
              <EmptyState
                titleKey="scheduledReports.list.emptyTitle"
                bodyKey="scheduledReports.historyEmptyBody"
              />
            }
          />
        )}
      </Card>
      <ConfirmDialog
        open={pendingDelete !== null}
        title={pendingDelete
          ? formatMessage("scheduledReports.delete.confirmTitle", { name: pendingDelete.name })
          : ""}
        body={t("scheduledReports.delete.confirmBody")}
        tone="danger"
        loading={remove.isPending}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => pendingDelete && remove.mutate(pendingDelete.id)}
      />
    </div>
  );
}
